import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { publicGet, publicPost } from 'helpers/requests';
import { buildPublicPortalPath, isPublicCatalogPath } from '../publicPortalRoutes';

export { isPublicCatalogPath };

/**
 * Data layer for the public CBAM Portal share page.
 *
 * The portal lives at /public/cbam-portal/:slug and is password-gated — no
 * opaque token in the URL. Two backend endpoints back it:
 *
 *   GET  /public/cbam-portal/:slug/meta
 *     Minimal portal identity so the password screen can confirm which
 *     operator the visitor is accessing before they type anything.
 *       200 → { operatorName, operatorLogoUrl, requiresPassword, lastUpdatedAt }
 *       404 → slug doesn't exist
 *       410 → portal disabled by the operator
 *
 *   POST /public/cbam-portal/:slug   body: { password: string | null }
 *     Returns the full payload on success. When requiresPassword is false the
 *     frontend posts { password: null } and the backend returns data.
 *       200 → full payload (operator + products[])
 *       401 → wrong / missing password (generic — don't distinguish cases)
 *       404 / 410 → as above
 *
 * Each product arrives fully computed from the backend: direct/indirect/total,
 * the precomputed sefa, the EU default factors (defaultDirect/defaultIndirect/
 * defaultTotal + defaultBenchmark/defaultSefa), and a `useDirect` flag that
 * selects the emissions base for cost calculations. The frontend no longer
 * derives SEFA locally.
 */

/**
 * Fetch the portal identity for the password screen. Kept as a query (not a
 * mutation) so it caches per slug and doesn't refetch on every render.
 */
export const useFetchPublicCatalogMeta = (slug, { surface = 'page' } = {}) => {
  return useQuery({
    queryKey: ['public-cbam-portal-meta', surface, slug],
    queryFn: async () => {
      const res = await publicGet(`${buildPublicPortalPath(slug, { surface })}/meta`);
      return res.data;
    },
    enabled: Boolean(slug),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Exchange a password for the full portal payload. Password travels in the
 * POST body only (never the query string). Pass null when the portal is not
 * password-protected.
 */
export const fetchPublicCatalogData = async (slug, password, { surface = 'page' } = {}) => {
  const res = await publicPost(buildPublicPortalPath(slug, { surface }), {
    password: password ?? null,
  });
  return res.data;
};

export const usePublicPortalAccess = (
  slug,
  {
    surface = 'page',
    passwordErrorMessage = 'That password didn’t work. Double-check the link your supplier sent you.',
  } = {},
) => {
  const metaQuery = useFetchPublicCatalogMeta(slug, { surface });
  const [payload, setPayload] = useState(null);
  // The password that the backend just accepted, kept for the lifetime of this
  // page ONLY. The portal gate is stateless — it re-verifies on every request
  // (no token, cookie or session is issued) — so any follow-up call that needs
  // the same authority (the PDF render + its status polls) must present it
  // again. Deliberately NOT localStorage/sessionStorage/cookie/URL: a portal
  // password is a shared secret the operator can rotate, and persisting it
  // would outlive the tab that was granted access.
  const [acceptedPassword, setAcceptedPassword] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const autoPostedRef = useRef(false);

  const dataMutation = useMutation({
    mutationFn: (password) => fetchPublicCatalogData(slug, password, { surface }),
    onSuccess: (data, password) => {
      setPayload(data);
      setAcceptedPassword(password ?? null);
      setPasswordError('');
    },
    onError: (error) => {
      if (error?.response?.status === 401) setPasswordError(passwordErrorMessage);
    },
  });

  useEffect(() => {
    setPayload(null);
    setAcceptedPassword(null);
    setPasswordError('');
    autoPostedRef.current = false;
  }, [slug, surface]);

  useEffect(() => {
    if (metaQuery.data?.requiresPassword === false && !autoPostedRef.current) {
      autoPostedRef.current = true;
      dataMutation.mutate(null);
    }
  }, [metaQuery.data, dataMutation]);

  const fatalError =
    metaQuery.isError || (dataMutation.isError && dataMutation.error?.response?.status !== 401);

  return {
    metaQuery,
    payload,
    acceptedPassword,
    passwordError,
    dataMutation,
    fatalError,
    submitPassword: (password) => {
      setPasswordError('');
      dataMutation.mutate(password);
    },
  };
};
